import debounce from "debounce-promise";
import { FeatureFlag } from "healthex-common/features.dtos";
import { transformToSearchResults, } from "../Infra/fasten.search.dtos";
import { getFastenApiMode, getFastenPublicId } from "../Infra/FastenSearchService";
function isEpicPlatformType(value) {
    return value !== undefined && String(value).toLowerCase() === "epic";
}
function resultHasEpicEndpoint(result) {
    var _a, _b;
    for (const portal of (_a = result.portals) !== null && _a !== void 0 ? _a : []) {
        const hasEpic = ((_b = portal.endpoints) !== null && _b !== void 0 ? _b : []).some((ep) => isEpicPlatformType(ep.platform_type));
        if (hasEpic)
            return true;
    }
    return false;
}
function normalizeNameForLogoCache(name) {
    return name.trim().toLowerCase();
}
function wouldBeFilteredAsEpic(result) {
    var _a, _b;
    let hasNonEpicPortal = false;
    for (const portal of (_a = result.portals) !== null && _a !== void 0 ? _a : []) {
        const nonEpicCount = ((_b = portal.endpoints) !== null && _b !== void 0 ? _b : []).filter((ep) => !isEpicPlatformType(ep.platform_type)).length;
        if (nonEpicCount > 0) {
            hasNonEpicPortal = true;
            break;
        }
    }
    return !hasNonEpicPortal;
}
function filterEpicFromResults(results) {
    var _a, _b;
    const filtered = [];
    for (const result of results) {
        const portals = [];
        for (const portal of (_a = result.portals) !== null && _a !== void 0 ? _a : []) {
            const allEndpoints = (_b = portal.endpoints) !== null && _b !== void 0 ? _b : [];
            const endpoints = allEndpoints.filter((ep) => !isEpicPlatformType(ep.platform_type));
            if (endpoints.length === 0)
                continue;
            if (endpoints.length < allEndpoints.length) {
                console.warn("[FastenSearchManager] Portal has both epic and non-epic endpoints; epic endpoints filtered out", {
                    portalId: portal.id,
                    portalName: portal.name,
                    resultId: result.id,
                    resultName: result.name,
                });
            }
            portals.push({ ...portal, endpoints });
        }
        if (portals.length > 0) {
            filtered.push({ ...result, portals });
        }
    }
    return filtered;
}
export class FastenSearchManager {
    constructor(fastenSearchService, environment, authManager, featureManager) {
        this.fastenSearchService = fastenSearchService;
        this.environment = environment;
        this.authManager = authManager;
        this.featureManager = featureManager;
        this.epicLogoCacheByNormalizedName = new Map();
        this.search = debounce(async (query, filters, searchAfter) => {
            try {
                let { results, nextCursor, totalHits } = await this.searchInternal(query, filters, searchAfter);
                const epicEnabled = this.featureManager.isFeatureEnabled(FeatureFlag.RO_EPIC_DIRECT);
                const config = this.featureManager.decodeFeatureConfiguration(FeatureFlag.RO_EPIC_DIRECT);
                const debugEntries = [];
                if (epicEnabled && (config === null || config === void 0 ? void 0 : config.filterEpicFromFasten)) {
                    if (config.useLogoCache) {
                        this.addToEpicLogoCache(results);
                    }
                    if (config.debug) {
                        debugEntries.push(...results.map((r) => {
                            var _a, _b;
                            return ({
                                name: (_a = r.name) !== null && _a !== void 0 ? _a : "",
                                score: (_b = r.score) !== null && _b !== void 0 ? _b : null,
                                filtered: wouldBeFilteredAsEpic(r) ? "F" : "",
                            });
                        }));
                    }
                    const initialCount = results.length;
                    results = filterEpicFromResults(results);
                    if (results.length < initialCount / 2 && nextCursor) {
                        const more = await this.searchInternal(query, filters, nextCursor);
                        if (config.useLogoCache) {
                            this.addToEpicLogoCache(more.results);
                        }
                        if (config.debug) {
                            debugEntries.push(...more.results.map((r) => {
                                var _a, _b;
                                return ({
                                    name: (_a = r.name) !== null && _a !== void 0 ? _a : "",
                                    score: (_b = r.score) !== null && _b !== void 0 ? _b : null,
                                    filtered: wouldBeFilteredAsEpic(r) ? "F" : "",
                                });
                            }));
                        }
                        results = results.concat(filterEpicFromResults(more.results));
                        nextCursor = more.nextCursor;
                    }
                    if (config.debug && debugEntries.length > 0) {
                        console.log("[FastenSearchManager] search results (debug)", debugEntries);
                    }
                }
                return { results, nextCursor, totalHits };
            }
            catch (error) {
                console.error(error);
                return { results: [], nextCursor: null, totalHits: 0 };
            }
        }, 300);
    }
    getLogoUrlByName(name) {
        var _a;
        if (!(name === null || name === void 0 ? void 0 : name.trim()))
            return null;
        return (_a = this.epicLogoCacheByNormalizedName.get(normalizeNameForLogoCache(name))) !== null && _a !== void 0 ? _a : null;
    }
    addToEpicLogoCache(results) {
        var _a;
        for (const result of results) {
            if (!resultHasEpicEndpoint(result) || !((_a = result.name) === null || _a === void 0 ? void 0 : _a.trim()) || !result.logoUrl)
                continue;
            this.epicLogoCacheByNormalizedName.set(normalizeNameForLogoCache(result.name), result.logoUrl);
        }
    }
    async searchInternal(query, filters, searchAfter) {
        const isSandbox = this.authManager.isSandbox;
        const fastenApiMode = getFastenApiMode(isSandbox);
        const fastenPublicId = getFastenPublicId(isSandbox, this.environment);
        const response = await this.fastenSearchService.search(query, fastenApiMode, fastenPublicId, filters, searchAfter);
        return transformToSearchResults(response, this.environment);
    }
}
